import { Numeric } from '@ezchops/helpers';
import { sheetResolution } from '@ezchops/musicsheet';
import { isEqual, sum, uniqWith } from 'lodash';
export class MusicTiming {
    signature;
    constructor(signature) {
        this.signature = signature;
    }
    get numerator() {
        return this.signature.numerator;
    }
    get denominator() {
        return this.signature.denominator;
    }
    get beatNote() {
        return this.denominator;
    }
    get tickNote() {
        return this.signature.tickNote ?? this.denominator;
    }
    get ticksPerBeat() {
        return this.tickNote / this.denominator;
    }
    get beatsPerMeasure() {
        return this.numerator;
    }
    get ticksPerMeasure() {
        return this.numerator * this.tickNote / this.denominator;
    }
    get measureValue() {
        return this.numerator / this.denominator;
    }
    measureTimestamp(measure) {
        const tick = this.ticksPerMeasure * measure;
        return this.tickTimestamp(tick);
    }
    fraction = (absTick) => {
        const absBeat = Math.floor(absTick / this.ticksPerBeat);
        const realMeasure = absTick / this.ticksPerMeasure;
        const [measure,] = Numeric.intAndFraction(realMeasure);
        const beat = absBeat % this.beatsPerMeasure;
        const tick = absTick % this.ticksPerBeat;
        return { measure, beat, tick };
    };
    timestampTick(timestamp) {
        return this.realValue(timestamp) * this.tickNote;
    }
    tickTimestamp(tick) {
        return this.timestamp(tick / this.tickNote);
    }
    get endTimestamp() {
        const endTick = this.endTick;
        return endTick
            ? this.tickTimestamp(endTick)
            : undefined;
    }
    get endTick() {
        return this.duration
            ? this.ticksPerMeasure * this.duration
            : undefined;
    }
    /*
     * @deprecated use endTick
     */
    get lastTick() {
        return this.endTick;
    }
    position = (timestamp) => {
        const realValue = this.timestampTick(timestamp) / this.tickNote;
        const absTick = Math.floor(realValue * this.tickNote);
        const { measure, beat, tick } = this.fraction(absTick);
        return {
            measure, beat, tick,
            realValue, absTick, timestamp
        };
    };
    tickPosition = (absTick) => {
        const { measure, beat, tick } = this.fraction(absTick);
        const realValue = absTick / this.tickNote;
        const timestamp = this.tickTimestamp(absTick);
        return {
            measure, beat, tick,
            realValue, absTick, timestamp
        };
    };
    snappedTickPosition = (absTick, snap) => {
        const snappedTick = snap(absTick);
        const { measure, beat, tick } = this.fraction(snappedTick);
        const realValue = snappedTick / this.tickNote;
        const timestamp = this.tickTimestamp(snappedTick);
        const lag = absTick - snappedTick;
        return {
            measure, beat, tick,
            realValue, absTick: snappedTick, timestamp,
            lag
        };
    };
    snappedPosition = (timestamp, snap) => {
        const realTick = this.timestampTick(timestamp);
        const absTick = snap(realTick);
        const lag = realTick - absTick;
        return {
            ...this.tickPosition(absTick),
            lag
        };
    };
    closestTickPosition(absTick) {
        return this.snappedTickPosition(absTick, Math.round);
    }
    lastTickPosition(absTick) {
        return this.snappedTickPosition(absTick, Math.floor);
    }
    closestPosition(timestamp) {
        return this.snappedPosition(timestamp, Math.round);
    }
    lastPosition(timestamp) {
        return this.snappedPosition(timestamp, Math.floor);
    }
    nextPosition(timestamp) {
        return this.snappedPosition(timestamp, Math.ceil);
    }
    nextTicks(timestamp, until) {
        const ticks = [];
        // eslint-disable-next-line prefer-const
        let { lag, ...nextPosition } = this.nextPosition(timestamp);
        while (nextPosition.timestamp <= until) {
            ticks.push(nextPosition);
            nextPosition = this.tickPosition(nextPosition.absTick + 1);
        }
        return ticks;
    }
}
export class ConstantTempoTiming extends MusicTiming {
    tempo;
    leadIn;
    leadOut;
    duration;
    offset;
    /**
     *
     * @param signature
     * @param tempo (bpm)
     * @param leadIn (bars)
     * @param leadOut (bars)
     * @param duration (bars)
     * @param offset (seconds)
     */
    constructor(signature, tempo, leadIn, leadOut, duration, offset = 0) {
        super(signature);
        this.tempo = tempo;
        this.leadIn = leadIn;
        this.leadOut = leadOut;
        this.duration = duration;
        this.offset = offset;
    }
    get props() {
        return {
            signature: this.signature,
            tempo: this.tempo
        };
    }
    get tickOffset() {
        return this.leadIn * this.ticksPerMeasure;
    }
    get valueOffset() {
        return this.tickOffset / this.tickNote;
    }
    get beatLength() {
        return 60 / this.tempo;
    }
    get wholeNoteLength() {
        return 4 * this.beatLength;
    }
    get measureLength() {
        return this.beatLength * this.signature.numerator;
    }
    get tickLength() {
        return this.wholeNoteLength / this.tickNote;
    }
    timestampTick(timestamp) {
        const timestampFromStart = timestamp - this.offset;
        const wholeNoteLength = this.wholeNoteLength;
        const realValue = timestampFromStart / wholeNoteLength;
        return (realValue * this.tickNote) - this.tickOffset;
    }
    tickTimestamp(absTick) {
        return (absTick + this.tickOffset) * this.tickLength + this.offset;
    }
    get notesPerSecond() {
        return this.tempo / (60 * this.beatNote);
    }
    get secondsPerNote() {
        return (60 * this.beatNote) / this.tempo;
    }
    realValue(timestamp) {
        return (timestamp - this.offset) * this.notesPerSecond - this.valueOffset;
    }
    timestamp(realValue) {
        return this.secondsPerNote * (realValue + this.valueOffset) + this.offset;
    }
    tempoAt(timestamp) {
        return this.tempo;
    }
    static sheetTiming(sheet, tempo, tickNote) {
        if (uniqWith(sheet.segments.map(s => s.signature), isEqual).length > 1) {
            throw new Error('Multiple signatures not supported yet');
        }
        const sheetTickNote = sheetResolution(sheet);
        if (tickNote != null && (tickNote < sheetTickNote || tickNote % sheetTickNote !== 0)) {
            throw new Error(`Tick note must be a multiple of ${sheetTickNote}`);
        }
        const signature = { ...sheet.segments[0].signature, tickNote: tickNote ?? sheetTickNote };
        const duration = sum(sheet.segments.map(s => {
            if (s.repeats == null) {
                throw new Error('Infinite repeats not supported yet');
            }
            return (s.repeats + 1) * s.measures.length;
        }));
        return new ConstantTempoTiming(signature, tempo, 0, 0, duration);
    }
}
export function timeSignatureToArray(timeSignature) {
    return timeSignature !== undefined
        ? [timeSignature.numerator, timeSignature.denominator, timeSignature.tickNote]
        : undefined;
}
export function arrayToTimeSignature(signature) {
    if (signature == undefined) {
        return undefined;
    }
    const [numerator, denominator, tickNote] = signature;
    return { numerator, denominator, tickNote };
}
//# sourceMappingURL=MusicTiming.js.map